<template>
  <!-- Estados -->
  <div v-if="loading" class="text-center py-5">
    <Loading v-if="loading" mensaje="Cargando deportes…" />
  </div>

  <div v-else-if="error" class="alert alert-danger" role="alert">
    <ErrorCarga :errorMsg="errorMsg" @retry="fetchCanchas" />
  </div>

  <div v-else id="canchas" class="card">
    <!-- Toolbar -->
    <div
      class="card-body py-3 d-flex justify-content-between align-items-center"
    >
      <h2 class="h5 mb-0 titulo_pagina">Canchas</h2>

      <button
        type="button"
        class="btn btn-success d-inline-flex align-items-center gap-1"
        @click="onAddCancha"
        :disabled="adding"
        aria-label="Agregar cancha"
        title="Agregar cancha"
      >
        <span class="material-symbols-rounded">add</span>
        Agregar
      </button>
    </div>

    <!-- Contenido -->
    <div class="card-body pt-0">
      <div
        v-if="canchas.length === 0"
        class="alert alert-info mb-0"
        role="alert"
      >
        <SinDatos
          mensaje="No hay cargadas por el momento. Presione el botón Agregar para empezar."
        />
      </div>

      <div v-else class="table-responsive fo-table-wrap">
        <table class="table table-sm table-hover align-middle mb-0 fo-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th class="fo-col-actions">Acciones</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="c in canchas" :key="c.id_cancha">
              <td>{{ c.descripcion }}</td>
              <td>
                <div class="fo-actions">
                  <button class="btn btn-outline-primary" @click="onEdit(c)">
                    <span class="material-symbols-rounded">edit</span> Editar
                  </button>

                  <button class="btn btn-outline-danger" @click="onDelete(c)">
                    <span class="material-symbols-rounded">delete_forever</span>
                    Eliminar
                  </button>

                  <button
                    class="btn btn-outline-success"
                    @click="onModificarHorarios(c)"
                  >
                    <span class="material-symbols-rounded">calendar_clock</span>
                    Modificar Horarios
                  </button>

                  <button
                    class="btn btn-outline-secondary"
                    @click="onDuplicarHorarios(c)"
                    :disabled="canchas.length < 2"
                    title="Duplicar horarios y precios a otra cancha"
                  >
                    <span class="material-symbols-rounded">content_copy</span>
                    Duplicar horarios
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <AgregarModificarCanchaModal
    v-if="adding"
    v-model="adding"
    :sport="sport"
    :cancha="canchaSelected"
    @saved="grabarCancha"
  />

  <DiasHorariosCancha
    v-if="modificarHorarios"
    v-model="modificarHorarios"
    :sport="sport"
    :cancha="canchaSelected"
    @saved="grabarDiasHorarios"
  />

  <BaseModal
    v-if="duplicarHorariosAbierto"
    v-model="duplicarHorariosAbierto"
    :title="`Duplicar horarios · ${canchaOrigenDuplicar?.descripcion ?? ''}`"
    ok-label="Duplicar"
    cancel-label="Cancelar"
    :loading="duplicandoHorarios"
    layout="modal"
    placement="center"
    @confirm="confirmarDuplicarHorarios"
    @cancel="cancelarDuplicarHorarios"
  >
    <div class="d-flex flex-column gap-3">
      <div>
        <label class="form-label fw-semibold">Cancha destino</label>
        <select
          class="form-select"
          v-model.number="idCanchaDestinoDuplicar"
          :disabled="duplicandoHorarios"
        >
          <option :value="null">Seleccionar cancha</option>
          <option
            v-for="c in canchasDestinoDuplicar"
            :key="c.id_cancha"
            :value="c.id_cancha"
          >
            {{ c.descripcion }}
          </option>
        </select>
      </div>

      <div class="alert alert-warning mb-0">
        Se copiarán <b>todos los días, horarios y precios</b> de
        <b>{{ canchaOrigenDuplicar?.descripcion }}</b> a la cancha destino.
        La configuración activa que tenga actualmente la cancha destino será
        reemplazada.
      </div>
    </div>
  </BaseModal>

  <MostrarMensaje
    v-model:modelValue="grabarHorariosDiasOk"
    status="success"
    message="Se han grabado correctamente los horarios/precios para la cancha"
    :duration="5000"
    :closable="true"
  />

  <MostrarMensaje
    v-model:modelValue="grabarHorariosDiasError"
    status="error"
    message="Ha ocurrido un error al grabar los horarios. Intente nuevamente por favor"
    :closable="false"
  />

  <MostrarMensaje
    v-model:modelValue="mostrarMensajeError"
    status="error"
    :message="mensajeError"
    :duration="8000"
    :closable="true"
  />

  <MostrarMensaje
    v-model:modelValue="mostrarMensajeOk"
    status="success"
    :message="mensajeOk"
    :duration="duracionMensajeOk"
    :closable="true"
  />
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";

import Loading from "@/components/Loading.vue";
import ErrorCarga from "@/components/ErrorCarga.vue";
import SinDatos from "@/components/SinDatos.vue";
import MostrarMensaje from "@/components/MostrarMensaje.vue";
import AgregarModificarCanchaModal from "@/components/AgregarModificarCanchaModal.vue";
import DiasHorariosCancha from "@/components/DiasHorariosCancha.vue";
import BaseModal from "@/components/BaseModal.vue";
import { confirm } from "@/composables/useConfirm";
import {
  apiGetCanchasPorDeporte,
  apiPostNuevaCancha,
  apiPutCancha,
  apiPostBorrarCancha,
  apiPostHorariosPreciosCanchas,
  apiCopiarConfiguracionCancha,
} from "@/api.js";

const loading = ref(true);
const error = ref(false);
const errorMsg = ref("");
const canchas = ref([]);
const adding = ref(false);
const canchaSelected = ref({});
const modificarHorarios = ref(false);
const duplicarHorariosAbierto = ref(false);
const duplicandoHorarios = ref(false);
const canchaOrigenDuplicar = ref(null);
const idCanchaDestinoDuplicar = ref(null);
const grabarHorariosDiasOk = ref(false);
const grabarHorariosDiasError = ref(false);
const mensajeError = ref("");
const mostrarMensajeError = ref(false);
const mostrarMensajeOk = ref(false);
const mensajeOk = ref("");
const duracionMensajeOk = ref(3000);
let controller = null;

const { sport } = defineProps({
  sport: { type: Object, required: true },
});

function onAddCancha() {
  adding.value = true;
  canchaSelected.value = {};
}

function onEdit(cancha) {
  adding.value = true;
  canchaSelected.value = cancha;
}

async function onDelete(cancha) {
  const ok = await confirm({
    title: "Eliminar cancha",
    message: "¿Seguro que querés eliminar esta cancha?",
    confirmText: "Sí, eliminar",
    cancelText: "No, cancelar",
    confirmClass: "btn-danger",
    panelClass: "w-100 w-md-50",
  });

  if (!ok) return;

  try {
    const idCancha = cancha.id_cancha;

    if (idCancha == null) {
      mensajeError.value = "No se pudo identificar la cancha a eliminar.";
      mostrarMensajeError.value = true;
      return;
    }

    await apiPostBorrarCancha({ idCancha }, { signal: controller?.signal });

    await fetchCanchas();

    mensajeOk.value = "La cancha se eliminó correctamente.";
    mostrarMensajeOk.value = true;
  } catch (e) {
    console.error(e);

    mensajeError.value = obtenerMensajeErrorApi(
      e,
      "No se pudo eliminar la cancha. Intente nuevamente por favor.",
    );

    mostrarMensajeError.value = true;
  } finally {
    loading.value = false;
  }
}

function onModificarHorarios(cancha) {
  canchaSelected.value = cancha;
  modificarHorarios.value = true;
}

const canchasDestinoDuplicar = computed(() => {
  const origen = Number(canchaOrigenDuplicar.value?.id_cancha);
  return canchas.value.filter((c) => Number(c.id_cancha) !== origen);
});

function onDuplicarHorarios(cancha) {
  canchaOrigenDuplicar.value = cancha;
  idCanchaDestinoDuplicar.value = null;
  duplicarHorariosAbierto.value = true;
}

function cancelarDuplicarHorarios() {
  duplicarHorariosAbierto.value = false;
  canchaOrigenDuplicar.value = null;
  idCanchaDestinoDuplicar.value = null;
}

async function confirmarDuplicarHorarios() {
  const origen = Number(canchaOrigenDuplicar.value?.id_cancha);
  const destino = Number(idCanchaDestinoDuplicar.value);

  if (!origen || !destino) {
    mensajeError.value = "Seleccioná una cancha destino.";
    mostrarMensajeError.value = true;
    return;
  }

  try {
    duplicandoHorarios.value = true;

    const resp = await apiCopiarConfiguracionCancha(
      {
        id_cancha_origen: origen,
        id_cancha_destino: destino,
      },
      { signal: controller?.signal },
    );

    duplicarHorariosAbierto.value = false;

    mensajeOk.value =
      resp?.message || "Los horarios y precios se duplicaron correctamente.";
    duracionMensajeOk.value = 5000;
    mostrarMensajeOk.value = true;

    canchaOrigenDuplicar.value = null;
    idCanchaDestinoDuplicar.value = null;
  } catch (e) {
    console.error(e);
    mensajeError.value = obtenerMensajeErrorApi(
      e,
      "No se pudieron duplicar los horarios. Intente nuevamente por favor.",
    );
    mostrarMensajeError.value = true;
  } finally {
    duplicandoHorarios.value = false;
  }
}

function obtenerMensajeErrorApi(e, fallback) {
  const texto = e?.message || "";

  // Caso típico actual:
  // HTTP 400 {"detail":"La cancha posee reservas a futuro..."}
  const jsonMatch = texto.match(/\{.*\}/);

  if (jsonMatch) {
    try {
      const data = JSON.parse(jsonMatch[0]);
      if (data?.detail) return data.detail;
    } catch {}
  }

  return fallback;
}

watch(
  () => sport?.id,
  (nuevo, viejo) => {
    if (nuevo && nuevo !== viejo) fetchCanchas();
  },
);

onMounted(fetchCanchas);

async function fetchCanchas() {
  const idDeporte = sport?.id;
  if (!idDeporte) return;

  loading.value = true;
  error.value = false;
  errorMsg.value = "";

  try {
    controller?.abort?.();
  } catch {}
  controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;

  try {
    const data = await apiGetCanchasPorDeporte(
      { idDeporte },
      { signal: controller?.signal },
    );
    const lista = Array.isArray(data)
      ? data
      : data?.results ?? data?.items ?? [];
    canchas.value = lista.map((s, i) => ({
      id_cancha: s.id_cancha ?? i,
      id_deporte: s.id_deporte,
      descripcion: s.descripcion ?? "",
    }));
  } catch (e) {
    console.error(e);
    error.value = true;
    errorMsg.value =
      "No se pudieron cargar las canchas. Intente nuevamente por favor.";
  } finally {
    loading.value = false;
  }
}

async function grabarCancha(payload) {
  const idDeporte = payload.idDeporte;
  const descripcion = payload.descripcion;
  const idCancha = payload.idCancha;

  try {
    if (idCancha == null) {
      await apiPostNuevaCancha(
        { idDeporte, descripcion },
        { signal: controller?.signal },
      );
    } else {
      await apiPutCancha(
        { idDeporte, descripcion, idCancha },
        { signal: controller?.signal },
      );
    }

    await fetchCanchas();
  } catch (e) {
    console.error(e);
  } finally {
    adding.value = false;
  }
}

async function grabarDiasHorarios(payload) {
  try {
    const data = await apiPostHorariosPreciosCanchas(
      { data: payload },
      { signal: controller?.signal },
    );

    if (data.ok) {
      grabarHorariosDiasOk.value = true;
      modificarHorarios.value = false;
    } else {
      grabarHorariosDiasError.value = true;
    }
  } catch (e) {
    const detalle = e.payload?.detail || e.message || "Ha ocurrido un error";
    mensajeError.value = detalle;
    mostrarMensajeError.value = true;
    console.error(e);
  }
}
</script>
